import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageBody } from "@/components/dashboard/DashboardShell";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Pencil,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { ProviderDetailSidebar } from "@/components/admin/ProviderDetailSidebar";
import { AdminBulkActionBar } from "@/components/admin/AdminBulkActionBar";

export const Route = createFileRoute("/dashboard/admin/listings/all")({
  head: () => ({
    meta: [{ title: "All Listings · Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: AllListingsPage,
});

const STATUS_OPTIONS = ["all", "unclaimed", "free", "premium", "lifetime"] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];

const FILTER_LABELS: Record<StatusFilter, string> = {
  all: "All",
  unclaimed: "Unclaimed",
  free: "Free",
  premium: "Premium",
  lifetime: "Lifetime",
};

const AI_FLAG_OPTIONS = ["all", "needs_review", "approved", "rejected"] as const;
type AiFlagFilter = (typeof AI_FLAG_OPTIONS)[number];

type ListingRow = {
  id: string;
  npi: string | null;
  practice: string;
  name: string;
  address: string;
  city: string;
  state: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  license_number: string | null;
  listing_status: string;
  plan: string | null;
  is_featured: boolean;
  is_active: boolean;
  created_at: string;
};

const PAGE_SIZE = 50;
const SELECT_COLS =
  "id, npi, practice, name, address, city, state, phone, fax, email, license_number, listing_status, plan, is_featured, is_active, created_at";

type DerivedStatus = "unclaimed" | "lifetime" | "premium" | "free";
const deriveStatus = (p: ListingRow): DerivedStatus => {
  if (p.listing_status === "unclaimed") return "unclaimed";
  if (p.plan === "lifetime_premium") return "lifetime";
  if (p.plan === "premium_monthly" || p.plan === "premium_yearly") return "premium";
  return "free";
};

const STATUS_STYLES: Record<DerivedStatus, string> = {
  unclaimed: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
  free: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  premium: "bg-violet-50 text-violet-700 ring-1 ring-violet-200",
  lifetime: "bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-200",
};

type ColumnFilters = {
  npi: string;
  practice: string;
  address: string;
  phone: string;
  fax: string;
  email: string;
  license: string;
};
const EMPTY_FILTERS: ColumnFilters = {
  npi: "",
  practice: "",
  address: "",
  phone: "",
  fax: "",
  email: "",
  license: "",
};
const STATUS_LABEL: Record<DerivedStatus, string> = {
  unclaimed: "Unclaimed",
  free: "Free",
  premium: "Premium",
  lifetime: "Lifetime",
};

type Cursor = { created_at: string; id: string } | null;

function AllListingsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ColumnFilters>(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState<ColumnFilters>(EMPTY_FILTERS);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [aiFlag, setAiFlag] = useState<AiFlagFilter>("all");
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());

  // Cursor stack — index 0 is page 1 (no cursor). cursorStack[pageIndex] is the
  // cursor used to fetch that page.
  const [cursorStack, setCursorStack] = useState<Cursor[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [rows, setRows] = useState<ListingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 500);
    return () => clearTimeout(t);
  }, [filters]);

  // Reset pagination whenever filters/status/aiFlag change
  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
  }, [debouncedFilters, status, aiFlag]);

  const setFilter = (k: keyof ColumnFilters, v: string) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const fetchPage = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErrorMsg(null);
    try {
      const { adminListProvidersFn } = await import("@/lib/providers-secure.functions");
      const cursor = cursorStack[pageIndex] ?? null;
      const { rows: fetched, hasMore: more } = await adminListProvidersFn({
        data: {
          cursor,
          status,
          aiFlag,
          filters: debouncedFilters,
          pageSize: PAGE_SIZE,
        },
      });
      if (myReq !== reqIdRef.current) return;
      setHasMore(more);
      setRows((fetched ?? []) as ListingRow[]);
    } catch (e: any) {
      if (myReq !== reqIdRef.current) return;
      setErrorMsg(e?.message ?? "Failed to load listings");
      setRows([]);
      setHasMore(false);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [pageIndex, cursorStack, status, aiFlag, debouncedFilters]);


  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const goNext = () => {
    if (!hasMore || rows.length === 0) return;
    const last = rows[rows.length - 1];
    const nextCursor: Cursor = { created_at: last.created_at, id: last.id };
    setCursorStack((s) => {
      const copy = s.slice(0, pageIndex + 1);
      copy.push(nextCursor);
      return copy;
    });
    setPageIndex((p) => p + 1);
  };

  const goPrev = () => {
    if (pageIndex === 0) return;
    setPageIndex((p) => p - 1);
  };

  const start = rows.length === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
  const end = pageIndex * PAGE_SIZE + rows.length;

  const deleteOne = async (id: string) => {
    if (!confirm("Delete this listing? This cannot be undone.")) return;
    try {
      const { adminDeleteProviderFn } = await import("@/lib/providers-secure.functions");
      await adminDeleteProviderFn({ data: { id } });
      toast.success("Listing deleted");
      fetchPage();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to delete");
    }
  };


  const toggleBulk = (id: string) =>
    setBulkSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allOnPageSelected = rows.length > 0 && rows.every((p) => bulkSelected.has(p.id));
  const toggleAllOnPage = () =>
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((p) => next.delete(p.id));
      else rows.forEach((p) => next.add(p.id));
      return next;
    });

  const colFilterInput = (k: keyof ColumnFilters, placeholder: string) => (
    <div className="relative">
      <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input
        value={filters[k]}
        onChange={(e) => setFilter(k, e.target.value)}
        placeholder={placeholder}
        className="w-full pl-6 pr-2 h-7 text-[11px] border border-border bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
      />
    </div>
  );

  return (
    <PageBody className="space-y-4">
      <section className="space-y-3">
        {bulkSelected.size > 0 && (
          <AdminBulkActionBar
            ids={[...bulkSelected]}
            onDone={() => { setBulkSelected(new Set()); fetchPage(); }}
          />
        )}
        <div className="card-surface-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1280px] border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 text-foreground border-b border-border">
                  <Th className="w-8">
                    <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage} />
                  </Th>
                  <Th>NPI</Th>
                  <Th>Practice</Th>
                  <Th>Address</Th>
                  <Th>Phone</Th>
                  <Th>Fax</Th>
                  <Th>Email</Th>
                  <Th>License #</Th>
                  <Th>Status</Th>
                  <Th className="text-right pr-4">Action</Th>
                </tr>
                <tr className="bg-muted/40 border-b border-border">
                  <FilterTh>{null}</FilterTh>
                  <FilterTh>{colFilterInput("npi", "NPI…")}</FilterTh>
                  <FilterTh>{colFilterInput("practice", "Practice or name…")}</FilterTh>
                  <FilterTh>{colFilterInput("address", "Address…")}</FilterTh>
                  <FilterTh>{colFilterInput("phone", "Phone…")}</FilterTh>
                  <FilterTh>{colFilterInput("fax", "Fax…")}</FilterTh>
                  <FilterTh>{colFilterInput("email", "Email…")}</FilterTh>
                  <FilterTh>{colFilterInput("license", "License…")}</FilterTh>
                  <FilterTh>
                    <div className="flex gap-1">
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as StatusFilter)}
                        className="w-full h-7 px-2 text-[11px] border border-border bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {FILTER_LABELS[s]}
                          </option>
                        ))}
                      </select>
                      <select
                        value={aiFlag}
                        onChange={(e) => setAiFlag(e.target.value as AiFlagFilter)}
                        className="w-full h-7 px-2 text-[11px] border border-border bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                      >
                        {AI_FLAG_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o === "all" ? "All photos" : `Photo: ${o.replace("_", " ")}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </FilterTh>
                  <FilterTh>
                    <button
                      onClick={() => {
                        setFilters(EMPTY_FILTERS);
                        setStatus("all");
                        setAiFlag("all");
                      }}
                      className="w-full h-7 px-2 text-[11px] border border-border bg-background hover:bg-muted rounded-md text-muted-foreground"
                    >
                      Clear
                    </button>
                  </FilterTh>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="border-b border-border animate-pulse">
                      {Array.from({ length: 10 }).map((__, j) => (
                        <td key={j} className="px-3 py-3 border-r border-border last:border-r-0">
                          <div className="h-3 w-20 bg-muted" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!loading &&
                  rows.map((p) => {
                    const st = deriveStatus(p);
                    return (
                      <tr
                        key={p.id}
                        onClick={() => setSelectedProviderId(p.id)}
                        className="border-b border-border hover:bg-accent/40 transition-colors cursor-pointer"
                      >
                        <Td>
                          <input
                            type="checkbox"
                            checked={bulkSelected.has(p.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleBulk(p.id)}
                          />
                        </Td>
                        <Td className="font-mono text-[11px] text-sky-700">{p.npi || "—"}</Td>
                        <Td className="font-semibold text-foreground">{p.practice || p.name}</Td>
                        <Td className="text-muted-foreground max-w-[220px] truncate">
                          {[p.address, p.city, p.state].filter(Boolean).join(", ") || "—"}
                        </Td>
                        <Td className="text-foreground">{p.phone || "—"}</Td>
                        <Td className="text-muted-foreground">{p.fax || "—"}</Td>
                        <Td className="text-teal-700 max-w-[200px] truncate">{p.email || "—"}</Td>
                        <Td className="font-mono text-[11px] text-fuchsia-700">{p.license_number || "—"}</Td>
                        <Td>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-sm ${STATUS_STYLES[st]}`}
                          >
                            {STATUS_LABEL[st]}
                          </span>
                        </Td>
                        <Td className="text-right pr-3">
                          <div
                            className="inline-flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="p-1.5 text-muted-foreground hover:text-indigo-700 hover:bg-indigo-50"
                              onClick={() => setSelectedProviderId(p.id)}
                              aria-label="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="p-1.5 text-muted-foreground hover:text-sky-700 hover:bg-sky-50"
                              onClick={() => navigate({ to: "/providers/$id", params: { id: p.id } })}
                              aria-label="View"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={() => deleteOne(p.id)}
                              aria-label="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </Td>
                      </tr>
                    );
                  })}

                {!loading && rows.length === 0 && !errorMsg && (
                  <tr>
                    <td colSpan={10} className="p-10 text-center text-sm text-muted-foreground">
                      No listings match your filters.
                    </td>
                  </tr>
                )}
                {errorMsg && (
                  <tr>
                    <td colSpan={10} className="p-6 text-center text-sm text-destructive">
                      <div className="space-y-2">
                        <div>{errorMsg}</div>
                        <Button size="sm" variant="outline" onClick={() => fetchPage()}>
                          Retry
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                </>
              ) : rows.length === 0 ? (
                <>No results</>
              ) : (
                <>
                  Showing{" "}
                  <span className="font-medium text-foreground">
                    {start.toLocaleString()}–{end.toLocaleString()}
                  </span>
                  {hasMore ? " (more available)" : " (end of results)"}
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageIndex === 0 || loading}
                onClick={goPrev}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page <span className="font-medium text-foreground">{pageIndex + 1}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore || loading}
                onClick={goNext}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ProviderDetailSidebar
        providerId={selectedProviderId}
        onClose={() => setSelectedProviderId(null)}
        onChanged={() => fetchPage()}
      />
    </PageBody>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide border-r border-border last:border-r-0 ${className}`}
    >
      {children}
    </th>
  );
}
function FilterTh({ children }: { children: React.ReactNode }) {
  return <th className="px-2 py-1.5 border-r border-border last:border-r-0">{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={`px-3 py-2 align-middle border-r border-border last:border-r-0 ${className}`}>
      {children}
    </td>
  );
}

